import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RightsCell from '../components/RightsCell';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import InputText from '../components/Form/Fields/InputText';
import FabGroup from '../components/FabGroup';
import CreateRoleModal from '../components/Modal/CreateRoleModal';
import RoleUsersModal from '../components/Modal/RoleUsersModal';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

export default function AdminRoles() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const { hasRight } = useAuth();
    const canCreate = hasRight('ADMIN_ROLES_CREATE');
    const canEdit = hasRight('ADMIN_ROLES_EDIT');
    const tableRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [editRole, setEditRole] = useState(null);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);

    useEffect(() => {
        setTitle(`${t('Roles')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('admin_roles.subtitle')}</span>`);
        document.title = t('Roles');
        setTitleIcon('ti-shield-lock');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([
        { label: t('Administration') },
        { label: t('Roles'), url: '/admin/roles' },
    ]);

    const handleSaved = () => tableRef.current?.reload();
    const openCreate  = () => { setEditRole(null); setShowModal(true); };
    const openEdit    = (row) => { setEditRole(row); setShowModal(true); };
    const openUsers   = (row) => { setSelectedRole(row); setShowUsersModal(true); };

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            className: 'text-center text-nowrap',
            width: '1%',
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: t('admin_roles.column.name'),
            accessor: 'name',
            // the only column that grows: the others shrink to their content (width 1%)
            width: '100%',
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => (
                <span style={{
                    fontSize: 12, fontWeight: 600,
                    background: '#e0e7ff', color: '#4338ca',
                    border: '1px solid #c7d2fe',
                    borderRadius: 20, padding: '3px 12px',
                }}>{row.name}</span>
            ),
        },
        {
            Header: t('admin_roles.column.rights'),
            accessor: 'rights',
            // wrapping badges: a minimum width so a row holds about 4 of them
            width: '1%',
            minWidth: '360px',
            Cell: (row) => <RightsCell rights={row.rights} />,
        },
        {
            Header: t('Users'),
            accessor: 'userCount',
            className: 'text-center text-nowrap',
            width: '1%',
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>{row.userCount}</span>,
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            width: '1%',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openEdit(row)}>{t('Edit')}</button>
                            </li>
                        )}
                        <li>
                            <button className="dropdown-item" onClick={() => openUsers(row)}>{t('admin_roles.action.view_users')}</button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderRoleCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <span style={{
                    alignSelf: 'flex-start', fontSize: 12, fontWeight: 600,
                    background: '#e0e7ff', color: '#4338ca',
                    border: '1px solid #c7d2fe',
                    borderRadius: 20, padding: '3px 12px',
                }}>{row.name}</span>

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <span style={{ fontSize: 12.5, color: '#374151' }}>{row.userCount} {t('Users')}</span>
                    {actionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    const buttons = canCreate
        ? [{ label: t('admin_roles.new_role'), className: 'btn-primary', icon: faCirclePlus, onClick: openCreate }]
        : [];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/admin/roles`}
                columns={columns}
                defaultSort="name"
                defaultDirection="asc"
                pageSize={20}
                emptyMessage={t('admin_roles.empty')}
                searchable={true}
                searchPlaceholder={t('admin_roles.search_placeholder')}
                enableCardView={true}
                renderCard={renderRoleCard}
            />
            <FabGroup buttons={buttons} />

            <CreateRoleModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSaved={handleSaved}
                role={editRole}
            />

            <RoleUsersModal
                show={showUsersModal}
                onClose={() => setShowUsersModal(false)}
                role={selectedRole}
            />
        </>
    );
}
